/**
 * Mocked message builders for different chatbot flow previews.
 * These are used to create pre-populated messages for mocked/preview flows.
 */

import type { MessageListItem } from '@/library/message/types';
import { MessageType, SystemOptionType, ConnectOptionType, ConnectOptionKind } from '@/library/message/enum';
import {
  createCategoryListMessage,
  createSystemMessage,
  createUserSystemMessage,
  createTransactionSelectMessage,
  createSelectedTransactionMessage,
  createConnectOptionSelectMessage,
} from '@/library/message/lib';
import { switcher } from '@/common/switcher';
import { createMockedTransactions } from './mockedTransactions';
import { ExternalTransactionType } from '@/common/bot/enum/ExternalTransactionType';
import { TransactionType } from '@/common/config';
import { getConnectOptionChannelLabel } from '@/common/connectOption.utils';

const MOCKED_CHATBOT_OPTIONS = [
  { id: 1, title: 'Account & Profile', kind: SystemOptionType.CATEGORY },
  { id: 2, title: 'Payments & Transactions', kind: SystemOptionType.CATEGORY },
  { id: 3, title: 'Technical Issues', kind: SystemOptionType.CATEGORY },
] as const;

const MOCKED_CHATBOT_SELECTED_OPTION = MOCKED_CHATBOT_OPTIONS[0];

/**
 * Create mocked messages for the chatbot page
 */
export const createMockedChatbotMessages = (): MessageListItem[] => {
  const welcomeMessage = createCategoryListMessage({
    id: 'mocked-category-base',
    message: 'Hi there! 👋 Welcome to our support center. How can I help you today?',
    options: [...MOCKED_CHATBOT_OPTIONS],
    selectedOption: MOCKED_CHATBOT_SELECTED_OPTION,
  });

  const userSelectionMessage = createUserSystemMessage({
    id: 'mocked-chatbot-user-selection',
    message: MOCKED_CHATBOT_SELECTED_OPTION.title,
  });

  return [welcomeMessage, userSelectionMessage];
};

/**
 * Create mocked messages for the transactions flow
 */
export const createMockedTransactionMessages = (): MessageListItem[] => {
  const userSelection = createUserSystemMessage({
    id: 'mocked-user-category-selection',
    message: 'I selected: Payments & Transactions',
    interactiveMessageParts: ['I selected: ', 'Payments & Transactions'],
  });

  const transactionSelectMessage = createTransactionSelectMessage({
    id: 'mocked-transaction-select',
    config: {
      connect: {
        loginRequired: false,
        liveChat: true,
        openRequest: true,
        voiceChat: true,
      },
    },
    preSelectedTransactions: [createMockedTransactions()[0]],
    txSubtypes: [
      ExternalTransactionType.DEPOSITS_FAILED,
      ExternalTransactionType.DEPOSITS_SUCCESSFUL,
      ExternalTransactionType.DEPOSITS_PENDING,
    ],
  });

  const selectedTransaction = createSelectedTransactionMessage({
    id: 'mocked-selected-transaction',
    transactions: [createMockedTransactions()[0]],
    transactionType: TransactionType.Deposit,
    message: 'You selected transaction TXN-12345678',
  });

  return [userSelection, transactionSelectMessage, selectedTransaction];
};

/**
 * Create mocked messages for connect option selection
 */
export const createMockedConnectOptionsMessages = (
  selectedOption: ConnectOptionKind = ConnectOptionType.OPEN_REQUEST
): MessageListItem[] => {
  const welcomeMessage = createCategoryListMessage({
    id: 'mocked-category-base',
    message: 'How would you like to get in touch with us?',
    options: [{ id: 1, title: 'Account & Profile', kind: SystemOptionType.CATEGORY }],
    selectedOption: { id: 1, title: 'Account & Profile', kind: SystemOptionType.CATEGORY },
  });

  const userSelection = createUserSystemMessage({
    id: 'mocked-user-selection',
    message: 'I need help with my account',
  });

  const connectOptionsMessage = createConnectOptionSelectMessage({
    id: 'mocked-connect-options',
    message: 'How would you like to get in touch with us?',
    showLiveChat: true,
    showVoiceChat: true,
    showOpenRequest: true,
    selectedOption,
  });

  const selectedConnectOption = createUserSystemMessage({
    id: 'mocked-selected-connect-option',
    message: getConnectOptionChannelLabel(selectedOption),
  });

  return [welcomeMessage, userSelection, connectOptionsMessage, selectedConnectOption];
};

/**
 * Create mocked messages for CSAT flow
 */
export const createMockedCsatMessages = (): MessageListItem[] => {
  const thankYouMessage = createSystemMessage({
    id: 'mocked-csat-thank-you',
    type: MessageType.DEFAULT,
    message: 'Thank you for chatting with us today!',
  });

  return [thankYouMessage];
};

/**
 * Get mocked messages based on registry ID
 */
export const getMockedMessagesByRegistryId = (registryId: string): MessageListItem[] =>
  switcher<string, MessageListItem[]>(registryId)
    .when('page:chatbot', () => createMockedChatbotMessages())
    .when('page:chatbot-transactions', () => createMockedTransactionMessages())
    .when('page:chatbot-live-chat', () => createMockedConnectOptionsMessages(ConnectOptionType.LIVE_CHAT))
    .when('page:chatbot-open-request', () => createMockedConnectOptionsMessages(ConnectOptionType.OPEN_REQUEST))
    .when('page:chatbot-call', () => createMockedConnectOptionsMessages(ConnectOptionType.VOICE_CALL))
    .when('page:csat', () => createMockedCsatMessages())
    .none(() => [])
    .match();
